import { Link } from "@tanstack/react-router";
import { useLocale, useLocaleSwitcher, LOCALES, t, tr } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type Props = {
  /** False when no Meta Pixel is configured: the consent banner never runs, so there is nothing to manage. */
  hasCookieConsent: boolean;
};

export function SiteFooter({ hasCookieConsent }: Props) {
  const locale = useLocale();
  const switchLocale = useLocaleSwitcher();
  const year = new Date().getFullYear();
  const search = locale === "en" ? {} : { lang: "fr" as const };

  const links: { to: string; label: string }[] = [
    { to: "/pricing", label: locale === "fr" ? "Tarifs" : "Pricing" },
    { to: "/terms", label: locale === "fr" ? "Conditions générales" : "Terms and conditions" },
    { to: "/privacy-policy", label: locale === "fr" ? "Politique de confidentialité" : "Privacy Policy" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <footer className="mt-24 bg-primary-500 text-[var(--background-main)]">
      <div className="mx-auto w-full max-w-[1360px] px-6 py-6 md:px-10">
        <nav className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-[14px] leading-[20px]">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to as any}
              search={search as any}
              className="transition-opacity hover:opacity-80"
            >
              {l.label}
            </Link>
          ))}
          {hasCookieConsent && (
            <button
              type="button"
              onClick={() => import("vanilla-cookieconsent").then((cc) => cc.showPreferences())}
              className="transition-opacity hover:opacity-80"
            >
              {tr(t.cookies.manage, locale)}
            </button>
          )}
          <div className="ml-2 flex items-center rounded-full border border-[var(--background-main)]/25 text-xs font-medium">
            {LOCALES.map((loc) => (
              <button
                key={loc}
                type="button"
                onClick={() => switchLocale(loc)}
                className={cn(
                  "inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-[10px] uppercase transition-colors focus-visible:ring-2 focus-visible:ring-[var(--background-main)] focus-visible:outline-none",
                  locale === loc
                    ? "bg-[var(--background-main)] text-primary-500"
                    : "text-[var(--background-main)]/80 hover:text-[var(--background-main)]",
                )}
              >
                {loc}
              </button>
            ))}
          </div>
        </nav>
        <div className="mt-3 flex flex-col items-start gap-2 md:flex-row md:items-start md:gap-8">
          <p className="shrink-0 text-[12px] leading-[18px] opacity-90">
            Matchamind © {year} Anatol Labs.{" "}
            {locale === "fr" ? "Tous droits réservés." : "All rights reserved."}
          </p>
          <details className="group w-full text-[12px] leading-[18px] opacity-90 md:open" open>
            <summary className="cursor-pointer list-none underline underline-offset-2 md:hidden">
              {locale === "fr" ? "Mentions légales" : "Legal notice"}
            </summary>
            <p className="mt-2 md:mt-0">
              {locale === "fr"
                ? "Matchamind est un outil complémentaire et ne remplace pas un avis professionnel, un diagnostic, un traitement, une psychothérapie ou des services de santé mentale. Demandez toujours l'avis de votre médecin pour toute question relative à votre santé."
                : "Matchamind is a complementary tool, it does not replace any professional opinion, diagnosis, treatment, psychotherapy, or mental health services. Always seek the advice of your doctor for any questions you have related to your health."}
            </p>
          </details>
        </div>
      </div>
    </footer>
  );
}